import { AxiosError } from 'axios';
import type { DocumentExtractionV2Invocation } from '@lifeomic/react-client';
import type { useApiClient } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import { isDocumentExtractionV2InFlight } from './DocumentExtractionV2Tasks';
import {
  DocumentExtractionV2InvokeBody,
  selectSourceTask,
} from './documentExtractionV2Source';

type ApiClient = ReturnType<typeof useApiClient>;

type DuplicateTaskDetail = {
  task_id?: string;
  status?: string;
};

export type BulkDocumentExtractionV2InvokeResult = {
  started: number;
  skipped: number;
  failed: number;
};

export const shouldSkipDocumentExtractionInvoke = (
  task: DocumentExtractionV2Invocation | null,
): boolean => {
  if (!task) {
    return false;
  }
  if (isDocumentExtractionV2InFlight(task)) {
    return true;
  }
  return task.status === 'completed';
};

const getDuplicateTaskDetail = (
  error: AxiosError<{ detail?: DuplicateTaskDetail }>,
): DuplicateTaskDetail | undefined => {
  if (error.response?.status !== 409) {
    return undefined;
  }
  return error.response.data?.detail;
};

export const buildDocumentExtractionV2InvokeBody = ({
  subjectId,
  projectId,
  documentReferenceId,
  forceRegenerate = false,
  reason = 'PHC-UI manual validation',
}: {
  subjectId: string;
  projectId: string;
  documentReferenceId: string;
  forceRegenerate?: boolean;
  reason?: string;
}): DocumentExtractionV2InvokeBody => ({
  subject_id: subjectId,
  project_id: projectId,
  source_resource_type: 'DocumentReference',
  source_resource_id: documentReferenceId,
  source_document_reference_id: documentReferenceId,
  include_clinic_only: true,
  force_regenerate: forceRegenerate,
  reason,
});

export const invokeDocumentExtractionV2 = async (
  apiClient: ApiClient,
  body: DocumentExtractionV2InvokeBody,
): Promise<{ task_id: string }> => {
  const response = (await apiClient.request(
    'POST /v1/agents-v2/document-extraction-v2/invoke',
    body as Parameters<typeof apiClient.request>[1],
  )) as { data: { task_id: string } };

  return response.data;
};

export const runBulkDocumentExtractionV2Invoke = async ({
  apiClient,
  subjectId,
  projectId,
  documentReferenceIds,
  tasks,
  reason = 'PHC-UI bulk manual validation',
}: {
  apiClient: ApiClient;
  subjectId: string;
  projectId: string;
  documentReferenceIds: string[];
  tasks: DocumentExtractionV2Invocation[];
  reason?: string;
}): Promise<BulkDocumentExtractionV2InvokeResult> => {
  const result: BulkDocumentExtractionV2InvokeResult = {
    started: 0,
    skipped: 0,
    failed: 0,
  };

  for (const documentReferenceId of documentReferenceIds) {
    const existingTask = selectSourceTask(
      tasks,
      'DocumentReference',
      documentReferenceId,
    );

    if (shouldSkipDocumentExtractionInvoke(existingTask)) {
      result.skipped += 1;
      continue;
    }

    try {
      await invokeDocumentExtractionV2(
        apiClient,
        buildDocumentExtractionV2InvokeBody({
          subjectId,
          projectId,
          documentReferenceId,
          reason,
        }),
      );
      result.started += 1;
    } catch (error) {
      if (
        getDuplicateTaskDetail(
          error as AxiosError<{ detail?: DuplicateTaskDetail }>,
        )
      ) {
        result.skipped += 1;
        continue;
      }
      result.failed += 1;
    }
  }

  return result;
};
